import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './DetailModal.css';

const DetailModal = ({ isOpen, onClose, data, type = 'project' }) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!data) return null;

    // Normalize data fields based on type
    const title = data.name || data.title;
    const description = data.longDescription || data.description;
    const icon = data.icon || (type === 'project' ? '🚀' : type === 'publication' ? '📄' : '🔬');

    return ReactDOM.createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="detail-modal-overlay" onClick={onClose}>
                    <motion.div
                        className="detail-modal-content"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button className="modal-close" onClick={onClose}>&times;</button>

                        <div className="modal-header">
                            <span className="modal-icon">{icon}</span>
                            <div className="modal-title-group">
                                <h2>{title}</h2>
                                {type === 'publication' && (
                                    <p className="modal-subtitle">
                                        {data.authors} • {data.venue} ({data.year})
                                    </p>
                                )}
                                {data.status && <span className="modal-status-badge">{data.status}</span>}
                            </div>
                        </div>

                        <div className="modal-body">
                            {/* Main Description / Abstract */}
                            <div className="modal-section">
                                <h3>{type === 'publication' ? 'Abstract' : 'Overview'}</h3>
                                <p className="modal-description">{description || data.abstract}</p>
                            </div>

                            {/* Metrics for Projects */}
                            {type === 'project' && data.metricLabel && (
                                <div className="modal-section">
                                    <h3>Key Metrics</h3>
                                    <div className="metrics-summary">
                                        <div className="metric">
                                            <span className="label">{data.metricLabel}:</span>
                                            <span className="value">{data.metricValue}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Tech Stack for Projects and Areas */}
                            {data.tech && (
                                <div className="modal-section">
                                    <h3>Technologies & Tools</h3>
                                    <div className="tech-tags">
                                        {data.tech.split(',').map((tag, i) => (
                                            <span key={i} className="tech-tag">{tag.trim()}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="modal-footer">
                                <div className="modal-links">
                                    {data.github && (
                                        <a href={data.github} target="_blank" rel="noopener noreferrer" className="modal-link github">
                                            GitHub Source
                                        </a>
                                    )}
                                    {(data.link || data.pdf) && (
                                        <a href={data.link || data.pdf} target="_blank" rel="noopener noreferrer" className="modal-link demo">
                                            {type === 'publication' ? 'View Paper' : 'Live Demo / Link'}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.getElementById('modal-root')
    );
};

export default DetailModal;
